import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from '@dataplace.ai/ui-components/organisms'
import { ReactComponent as Pin } from '@dataplace.ai/assets/lib/icons/pinIcon.svg'
import { useSelector } from 'react-redux'
import { RootState } from 'apps/placeme/src/redux/store'
import {
  getAnwRadarChartData,
  getRadarChartLabels,
  NoPrint,
  RadarChart,
  RangeChart,
  TagButton,
  TileFormRowWithData,
  TileFormSpan,
  Tooltip,
} from '@dataplace.ai/ui-components/atoms'
import RankingModal from '@placeme/components/molecules/RankingModal/RankingModal'
import { WfPotentialModel } from '../WellFitPotentialModel'
import {
  AddressSpan,
  ChartTitle,
  Estimation,
  EstimationLabel,
  EstimationParagraph,
  RegularSpan,
  Section,
  SectionMainText,
  SectionTitle,
  SimilarAdressWrapper,
  TagButtonWrapper,
  Wrapper,
} from './WellFitPotentialModelResult.styles'
import { PotentialLabels } from '../../../BigBen/Potential/data'
import { similarLocationlabels } from '../resultData/labels'
import { AdditionalRanking } from './AdditionalRanking'

interface IWellFitPotentialModelResult {
  data: WfPotentialModel
  labels: PotentialLabels
}

const WellFitPotentialModelResult: React.FC<IWellFitPotentialModelResult> = ({
  data,
  labels,
}) => {
  const { t } = useTranslation()
  const { value } = useSelector((state: RootState) => state.location)
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [additionalRankingOpen, setAdditionalRankingOpen] = useState<boolean>(false)

  const { potentialModel } = data

  const memoizedData = useMemo(() => getAnwRadarChartData(potentialModel?.influence), [
    potentialModel?.influence,
  ])

  const memoizedLabels = useMemo(() => getRadarChartLabels(potentialModel?.influence, labels), [
    potentialModel?.influence,
    labels,
  ])

  const similarLocationsContent = useMemo(
    () =>
      potentialModel.similarLocations.map(
        (location) => [
          <AddressSpan key={location.address}>
            <Tooltip
              content={location.address}
              position='right center'
            >
              {location.address}
            </Tooltip>
          </AddressSpan>,
          <RegularSpan key={location.score}>{location.score}</RegularSpan>,
        ],
      ),
    [potentialModel.similarLocations],
  )

  const additionalValuesContent = useMemo(() => {
    const { additionalValues } = data.potentialModel

    return Object.entries(additionalValues).flatMap(
      ([category, categoryData]) =>
        Object.entries(categoryData as Record<string, { value: number; chainMeanValue: number }>).map(
          ([key, item]) => {
            const isBehavioral = category === 'behavioral'
            const value = isBehavioral ? `${(item.value * 100).toFixed()}%` : item.value.toFixed()
            const mean = isBehavioral ? `${(item.chainMeanValue * 100).toFixed()}%` : item.chainMeanValue.toFixed()
            return [
              <RegularSpan key={`${category}-${key}-name`}>
                {t(`placeme.well_fit.additional_values.${category}.${key}`)}
              </RegularSpan>,
              <RegularSpan key={`${category}-${key}-value`}>{value}</RegularSpan>,
              <RegularSpan key={`${category}-${key}-mean`}>{mean}</RegularSpan>,
            ]
          },
        ),
    )
  }, [data, t])

  return (
    <Wrapper>
      <Estimation>
        <EstimationParagraph>
          {t('placeme.well_fit.potential')}
          {' '}
          <strong>{potentialModel.score}</strong>
        </EstimationParagraph>
        <EstimationLabel>{t('placeme.well_fit.potential.label')}</EstimationLabel>
        <EstimationParagraph>
          {t('placeme.well_fit.potential.predicted_members')}
          {' '}
          <strong>{potentialModel.members}</strong>
        </EstimationParagraph>
        <EstimationLabel>{t('placeme.well_fit.potential.predictad_members.label')}</EstimationLabel>
      </Estimation>
      <ChartTitle>{t('placeme.anw_sales_potential_tile.radar.title')}</ChartTitle>
      <RadarChart
        data={memoizedData}
        height={450}
        labels={memoizedLabels}
      />
      <Section className='page-break-before'>
        <SectionTitle>{t('placeme.well_fit_potential_ranking.section_title_1')}</SectionTitle>
        <SectionMainText>{t('placeme.well_fit_potential_ranking.line_2')}</SectionMainText>
        <RangeChart
          label='generic.place'
          max={potentialModel.ranking.places.lowest}
          value={potentialModel.ranking.places.current}
        />
        <NoPrint>
          <TagButtonWrapper>
            <TagButton onClick={() => setModalIsOpen(true)}>
              <Pin />
              <p>{t('generic.see_ranking')}</p>
            </TagButton>
          </TagButtonWrapper>
        </NoPrint>
        <div className='page-break-after'>
          <RankingModal
            currentPosition={potentialModel.ranking.places.current}
            isOpen={modalIsOpen}
            rankingList={potentialModel.ranking.list}
            setIsOpen={setModalIsOpen}
          />
        </div>
      </Section>
      <Section>
        <TileFormRowWithData>
          <SectionTitle>{t('placeme.well_fit_similar_locations.row_with_data_1_span_1')}</SectionTitle>
        </TileFormRowWithData>
        <TileFormSpan>
          {t('placeme.well_fit_similar_locations.form_span_1_normal_1')}
        </TileFormSpan>
        <TileFormSpan
          isBold
          margin='20px 0 0 0'
        >
          {t('placeme.well_fit_similar_locations.form_span_full', {
            address: value?.address,
          })}
        </TileFormSpan>
        <SimilarAdressWrapper>
          <Table
            content={similarLocationsContent}
            gap='0.5rem'
            headerTemplate='3fr 1fr'
            labels={similarLocationlabels.map((label) => <span key={label}>{t(label)}</span>)}
            rowTemplate='3fr 1fr'
          />
        </SimilarAdressWrapper>
      </Section>
      <Section>
        <AdditionalRanking
          additionalRankingOpen={additionalRankingOpen}
          setAdditionalRankingOpen={setAdditionalRankingOpen}
          tableContent={additionalValuesContent}
        />
      </Section>
    </Wrapper>
  )
}

export default WellFitPotentialModelResult
